import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { findAll, remove, save } from "./api-disciplina";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import DisciplinaForm from "./DisciplinaForm";
import DisciplinaTable from "./DisciplinaTable";

const porDescricao = (a, b) => (a.descricao ?? "").localeCompare(b.descricao ?? "", "pt-BR");
const ordenar = (lista) => [...lista].sort(porDescricao);
const mensagemDe = (data, padrao) =>
  [].concat(data?.message ?? []).filter(Boolean).join("\n") || padrao;
const normalizar = (texto) =>
  (texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default function DisciplinaContainer() {
  const [disciplinas, setDisciplinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    findAll()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(mensagemDe(data, "Não foi possível carregar as disciplinas."));
        } else if (Array.isArray(data)) {
          setDisciplinas(ordenar(data));
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = (persist) => {
    const eNovo = !persist.codDisciplina;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível salvar a disciplina."));
          return;
        }
        setDisciplinas((prevState) =>
          ordenar(
            eNovo
              ? [...prevState, data]
              : prevState.map((d) => (d.codDisciplina === data.codDisciplina ? data : d)),
          ),
        );
        setEditando(null);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = (disciplina) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a disciplina "${disciplina.descricao}"?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(disciplina.codDisciplina).then((data) => {
              if (!data || data.error) {
                errorMessage(
                  mensagemDe(
                    data,
                    "A disciplina não pode ser removida pois está cadastrada em turmas. Remova a disciplina das turmas antes de excluí-la.",
                  ),
                );
                return;
              }
              setDisciplinas((prevState) => prevState.filter((d) => d.codDisciplina !== disciplina.codDisciplina));
              success("Disciplina removida.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  const termo = normalizar(busca.trim());
  const filtradas = termo ? disciplinas.filter((d) => normalizar(d.descricao).includes(termo)) : disciplinas;

  return (
    <Container>
      <DisciplinaForm
        disciplina={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Disciplinas</Typography>
          <Typography variant="body2" color="text.secondary">
            Disciplinas da instituição, usadas no cadastro de turmas e professores.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ eNovo: true })}>
          Nova Disciplina
        </Button>
      </Stack>

      {disciplinas.length > 0 && (
        <TextField
          size="small"
          placeholder="Buscar disciplina"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          sx={{ mb: 2, width: { xs: "100%", sm: 320 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}

      <DisciplinaTable
        disciplinas={filtradas}
        isLoading={isLoading}
        emptyText={termo ? "Nenhuma disciplina encontrada para a busca." : "Nenhuma disciplina cadastrada."}
        onEdit={setEditando}
        onDelete={handleDelete}
      />
    </Container>
  );
}
